import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Download, Trash2, AlertTriangle, X, Mail, MessageSquare } from 'lucide-react';
import firebaseService from '../services/firebaseService';

export default function PrivacySettings({ user, onClose, onOpenContact }) {
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Lukk med Escape (lukker bekreftelse først hvis den er åpen)
  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'Escape') return;
    if (showDeleteConfirm) {
      setShowDeleteConfirm(false);
    } else {
      onClose();
    }
  }, [showDeleteConfirm, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleDeleteAllData = async () => {
    setLoading(true);

    try {
      await firebaseService.deleteAllUserData(user.uid);
      localStorage.removeItem('fiskelykke-privacy-consent');
      localStorage.removeItem('fiskelykke-privacy-settings');
      localStorage.removeItem('fiskelykke-install-dismissed');
      alert('Alle dine data er slettet. Du blir nå logget ut.');
      window.location.reload();
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Feil ved sletting av data. Prøv igjen eller kontakt support.');
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[400] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 pt-safe"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-settings-title"
    >
      <div
        className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Shield className="text-[#E67E22]" size={24} />
            <h2 id="privacy-settings-title" className="font-serif text-2xl font-bold text-[#3B4D3C]">
              Personverninnstillinger
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Lukk personverninnstillinger"
            className="text-gray-500 hover:text-gray-700 rounded-full p-1 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E67E22]"
          >
            <X size={22} />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Intro */}
          <p className="text-sm text-gray-600 leading-relaxed">
            FiskDa lagrer fangstdataene dine sikkert, og nøyaktige posisjoner deles aldri uten
            ditt samtykke. Her kan du laste ned eller slette alt vi har lagret om deg.
          </p>

          {/* Dine data */}
          <section>
            <h3 className="font-serif text-xl font-bold text-[#3B4D3C] mb-4">Dine data</h3>

            <div className="space-y-4">
              <div className="p-4 border border-[#D1CCC0] rounded-lg">
                <h4 className="font-bold mb-2 flex items-center gap-2">
                  <Download size={16} />
                  Eksporter alle data
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Last ned alle dine fangstdata i JSON-format. Inkluderer bilder, posisjoner og metadata.
                </p>
                <button
                  disabled
                  className="bg-gray-200 text-gray-500 px-4 py-2 rounded cursor-not-allowed flex items-center gap-2"
                >
                  <Download size={16} />
                  Kommer snart
                </button>
              </div>

              <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
                <h4 className="font-bold mb-2 flex items-center gap-2 text-red-800">
                  <AlertTriangle size={16} />
                  Slett alle data
                </h4>
                <p className="text-sm text-red-700 mb-4">
                  Permanent sletting av alle dine fangster, bilder og kontodata. Denne handlingen kan ikke angres.
                </p>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center gap-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2"
                >
                  <Trash2 size={16} />
                  Slett alle data
                </button>
              </div>
            </div>
          </section>

          {/* Kontaktinfo */}
          <section className="bg-[#F8F5F0] p-4 rounded-lg">
            <h4 className="font-bold mb-2">Spørsmål om personvern?</h4>
            <p className="text-sm text-gray-600 mb-3">
              Send oss en e-post, så hjelper vi deg så fort vi kan.
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <a
                href="mailto:tmfoght@gmail.com"
                className="inline-flex items-center gap-2 text-[#E67E22] hover:underline font-semibold"
              >
                <Mail size={16} />
                tmfoght@gmail.com
              </a>
              {onOpenContact && (
                <button
                  onClick={() => {
                    onClose();
                    onOpenContact();
                  }}
                  className="inline-flex items-center gap-2 text-[#3B4D3C] hover:underline font-semibold"
                >
                  <MessageSquare size={16} />
                  Send oss en melding
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Sist oppdatert: {new Date().toLocaleDateString('no-NO')}
            </p>
          </section>

          {/* Produsent */}
          <p className="text-center text-[11px] text-gray-400/80 tracking-wide pt-2">
            Produkt fra DigitalVarde
          </p>
        </div>
      </div>

      {/* Slette-bekreftelse */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-[500] bg-black/80 flex items-center justify-center p-4"
          onClick={() => !loading && setShowDeleteConfirm(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-confirm-title"
        >
          <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4 text-red-600">
              <AlertTriangle size={24} />
              <h3 id="delete-confirm-title" className="font-bold text-lg">Bekreft sletting</h3>
            </div>
            <p className="mb-6 text-gray-700">
              Er du sikker på at du vil slette alle dine data permanent?
              Dette inkluderer alle fangster, bilder og kontoopplysninger.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteAllData}
                disabled={loading}
                className="flex-1 bg-red-600 text-white py-2 rounded hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Sletter...' : 'Ja, slett alt'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={loading}
                className="flex-1 bg-gray-200 text-gray-800 py-2 rounded hover:bg-gray-300 disabled:opacity-50 transition-colors"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
